import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  TemplateRef,
  computed,
  effect,
  input,
  model,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MdwButton } from '@spaces-ui/meadow/components/button';
import { MdwChip } from '@spaces-ui/meadow/components/chip';
import { MdwInput } from '@spaces-ui/meadow/components/input';
import { MdwListItem } from '@spaces-ui/meadow/components/list-item';
import { MdwTooltipIfClipped } from '@spaces-ui/meadow/components/tooltip';
import { MdwIcon } from '@spaces-ui/meadow/icons';
import {
  BadgeNumber_1,
  BadgeNumber_2,
  BadgeNumber_3,
  BadgeNumber_4,
  BadgeNumber_5,
  BadgeNumber_6,
  BadgeNumber_7,
  BadgeNumber_8,
  BadgeNumber_9,
  BadgeNumber_9plus,
  Close_Large,
  DropdownArrow,
} from '@spaces-ui/meadow/icons/svgs';
import { alphaSortByProperty, EventUtils, sort } from '@spaces-ui/utils';

export interface SelectOption<T, V> {
  label: string;
  value: V;
  rawOption: T;
}

@Component({
  selector: 'mdw-select',
  imports: [
    CommonModule,
    ScrollingModule,
    MdwListItem,
    MdwIcon,
    MdwButton,
    MdwChip,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    FormsModule,
    MdwInput,
    MdwTooltipIfClipped,
    MatTooltipModule,
  ],
  templateUrl: './select.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './select.scss',
})
export class MdwSelect<T, V> implements ControlValueAccessor {
  public options = input<Array<T | string>>([]);
  public placeholder = input('');
  public label = input('');
  public description = input('');
  public multiSelect = input(false);
  public enableSearch = input(false);
  public searchPlaceholder = input('');
  public clearSearchOnSelect = input(true);
  public searchProperties = input<Array<keyof T>>([]); // if not provided, will search all properties
  public labelProperty = input<keyof T>();
  public valueProperty = input<keyof T>();
  public sortProperty = input<keyof T>();
  public triggerMode = input<'default' | 'filter'>('default');
  public filterTriggerSize = input<'medium' | 'small'>('small');
  public templateRef = input<TemplateRef<unknown>>();
  public selectedItemTemplateRef = input<TemplateRef<unknown>>();
  public allowSelectedItemDismiss = input(true);
  public maxSelectedItemTemplateSize = input(192);
  public customErrorMessages = input<{ [key: string]: string }>({});
  public menuWidth = input<number>();
  public menuMaxHeight = input(256);
  public virtualItemSize = input(36);

  public icons = {
    DropdownArrow,
    Close_Large,
    BadgeNumber_1,
    BadgeNumber_2,
    BadgeNumber_3,
    BadgeNumber_4,
    BadgeNumber_5,
    BadgeNumber_6,
    BadgeNumber_7,
    BadgeNumber_8,
    BadgeNumber_9,
    BadgeNumber_9plus,
  };
  public isDisabled = signal(false);
  public value?: Array<V> | V = this.multiSelect() ? ([] as Array<V>) : undefined;
  public search = model('');

  public isOpen = signal(false);
  public triggerOrigin = viewChild(CdkOverlayOrigin);
  public menuRef = viewChild<ElementRef<HTMLElement>>('menuEl');
  // Position the dropdown below the trigger, flipping above when there is not
  // enough room (mirrors CDK Menu's STANDARD_DROPDOWN_BELOW_POSITIONS).
  public overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
  ];
  public selectedValueIds = signal<V[]>([]);
  public activeIndex = signal<number>(-1);
  public virtualScrollViewport = viewChild(CdkVirtualScrollViewport);
  public searchInputRef = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  private static instanceCounter = 0;
  public readonly instanceId = `mdw-select-${MdwSelect.instanceCounter++}`;

  public activeDescendantId = computed(() => {
    const index = this.activeIndex();
    if (index < 0) {
      return null;
    }
    return `${this.instanceId}-option-${index}`;
  });

  public listOptions = computed<Array<SelectOption<T, V>>>(() => {
    const labelProperty = this.labelProperty();
    const valueProperty = this.valueProperty();
    const sortProperty = this.sortProperty();
    const options = this.options();
    const firstOption = options[0];
    const isString = typeof firstOption === 'string';
    const sortedOptions = sortProperty
      ? isString
        ? sort(options as string[], false)
        : alphaSortByProperty<T>(options as T[], sortProperty, false)
      : this.options();

    return sortedOptions.map(
      option =>
        ({
          label: this.usesStringArray()
            ? (option as string)
            : labelProperty
              ? (option as T)[labelProperty]
              : '',
          value: this.usesStringArray()
            ? (option as string)
            : valueProperty
              ? (option as T)[valueProperty]
              : option,
          rawOption: option,
        }) as SelectOption<T, V>,
    );
  });

  public filteredListOptions = computed<Array<SelectOption<T, V>>>(() => {
    const options = this.listOptions();
    const search = this.search().toLowerCase();
    const searchProperties = this.searchProperties();

    if (search.length === 0) {
      return options;
    }

    return options.filter(option => {
      if (searchProperties.length > 0) {
        const searchText = searchProperties
          .map(prop => option.rawOption[prop])
          .join(' ')
          .toLowerCase();
        return searchText.includes(search);
      }
      return JSON.stringify(option).toLowerCase().includes(search);
    });
  });

  public noItems = computed(() => {
    return this.search().length > 0 && this.filteredListOptions().length === 0;
  });

  public selectedValues = computed<Array<SelectOption<T, V>>>(() => {
    const ids = this.selectedValueIds();
    const options = this.listOptions();
    return options.filter(o => ids.includes(o.value));
  });

  public selectedValueDisplayField = computed(() => {
    const value = this.selectedValues();
    return value.map(v => v.label).join(', ');
  });
  private usesStringArray = computed(() => typeof this.options()[0] === 'string');

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  onChange = (_: V | V[]) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  @HostListener('document:keydown', ['$event'])
  public handleKeyDown(event: KeyboardEvent) {
    // When enableSearch is true, the search input handles all keyboard events
    if (this.enableSearch()) {
      return;
    }

    // Only continue if the select menu is open and no other input is focused
    if (!this.isOpen() || document.activeElement?.tagName === 'INPUT') {
      return;
    }

    const ignoreKeys = ['ArrowLeft', 'ArrowRight', 'Control', 'Shift', 'Alt', 'Meta', 'CapsLock'];

    if (ignoreKeys.includes(event.key)) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown': {
        EventUtils.preventBubble(event);
        this.moveActiveIndex(1);
        break;
      }
      case 'ArrowUp': {
        EventUtils.preventBubble(event);
        this.moveActiveIndex(-1);
        break;
      }
      case 'Enter': {
        EventUtils.preventBubble(event);
        if (this.multiSelect() && this.activeIndex() === -1) {
          this.selectAll();
        } else {
          this.selectActiveOption();
        }
        break;
      }
      case ' ': {
        if (this.search().length === 0 && this.activeIndex() >= 0) {
          EventUtils.preventBubble(event);
          this.selectActiveOption();
        } else {
          this.search.set(this.search() + event.key);
        }
        break;
      }
      case 'Tab': {
        if (this.multiSelect()) {
          EventUtils.preventBubble(event);
          if (this.activeIndex() >= 0) {
            this.selectActiveOption();
          }
        } else if (this.activeIndex() >= 0) {
          EventUtils.preventBubble(event);
          this.selectActiveOption();
          this.close();
        }
        break;
      }
      case 'Home': {
        EventUtils.preventBubble(event);
        this.setActiveIndex(0);
        break;
      }
      case 'End': {
        EventUtils.preventBubble(event);
        const maxIndex = this.filteredListOptions().length - 1;
        if (maxIndex >= 0) {
          this.setActiveIndex(maxIndex);
        }
        break;
      }
      case 'Escape': {
        EventUtils.preventBubble(event);
        this.close();
        break;
      }
      case 'Backspace': {
        this.search.set(this.search().slice(0, -1));
        break;
      }
      default: {
        if (event.key.length === 1) {
          this.search.set(this.search() + event.key);
        }
        break;
      }
    }
  }

  public handleSearchKeyDown(event: KeyboardEvent): void {
    const options = this.filteredListOptions();
    const maxIndex = options.length - 1;

    switch (event.key) {
      case 'ArrowDown': {
        EventUtils.preventBubble(event);
        this.moveActiveIndex(1);
        break;
      }
      case 'ArrowUp': {
        EventUtils.preventBubble(event);
        this.moveActiveIndex(-1);
        break;
      }
      case 'Enter': {
        EventUtils.preventBubble(event);
        if (this.multiSelect() && this.activeIndex() === -1) {
          this.selectAll();
        } else {
          this.selectActiveOption();
        }
        break;
      }
      case ' ': {
        if (!this.multiSelect() && this.search().length === 0 && this.activeIndex() >= 0) {
          EventUtils.preventBubble(event);
          this.selectActiveOption();
        }
        break;
      }
      case 'Tab': {
        if (this.multiSelect()) {
          EventUtils.preventBubble(event);
          if (this.activeIndex() >= 0 && this.activeIndex() <= maxIndex) {
            this.selectActiveOption();
          }
        } else if (this.activeIndex() >= 0 && this.activeIndex() <= maxIndex) {
          EventUtils.preventBubble(event);
          this.selectActiveOption();
          this.close();
        }
        break;
      }
      case 'Home': {
        EventUtils.preventBubble(event);
        this.setActiveIndex(0);
        break;
      }
      case 'End': {
        EventUtils.preventBubble(event);
        if (maxIndex >= 0) {
          this.setActiveIndex(maxIndex);
        }
        break;
      }
      case 'Escape': {
        EventUtils.preventBubble(event);
        this.close();
        break;
      }
    }
  }

  // eslint-disable-next-line @angular-eslint/prefer-inject
  public constructor(public ngControl: NgControl) {
    ngControl.valueAccessor = this;

    effect(() => {
      this.search();
      this.activeIndex.set(-1);
    });
  }

  writeValue(value: V | V[] | null | undefined): void {
    if (this.multiSelect()) {
      const normalizedValue = Array.isArray(value) ? value : [];
      this.value = normalizedValue;
      this.selectedValueIds.set(normalizedValue);
    } else {
      this.value = value as V | undefined;
      this.selectedValueIds.set(value != null && value !== '' ? [value as V] : []);
    }
  }

  public handleSelectionChange(values: V[]) {
    this.selectedValueIds.set(values);

    if (this.multiSelect()) {
      this.value = values;
    } else {
      this.value = values[0];
    }

    this.onChange(this.value);
    this.onTouched();
  }

  public onOptionSelected(optionValue: V) {
    if (this.multiSelect()) {
      const currentIds = this.selectedValueIds();
      if (currentIds.includes(optionValue)) {
        this.handleSelectionChange(currentIds.filter(id => id !== optionValue));
      } else {
        this.handleSelectionChange([...currentIds, optionValue]);
      }
    } else {
      this.handleSelectionChange([optionValue]);
      this.close();
    }

    const clickedIndex = this.filteredListOptions().findIndex(o => o.value === optionValue);
    if (clickedIndex >= 0) {
      this.activeIndex.set(clickedIndex);
    }
  }

  removeSelectedOption(option: SelectOption<T, V>, event: MouseEvent) {
    EventUtils.preventBubble(event);

    const currentIds = this.selectedValueIds();
    this.handleSelectionChange(currentIds.filter(id => id !== option.value));
  }

  registerOnChange(fn: (_: V | V[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  public trackOption(_index: number, option: SelectOption<T, V>) {
    return option.value;
  }

  public selectAll() {
    const currentIds = this.selectedValueIds();
    const filtered = this.filteredListOptions();
    const filteredIds = filtered.map(o => o.value);

    const allSelected = filteredIds.every(id => currentIds.includes(id));

    if (allSelected) {
      // Deselect all filtered options
      this.handleSelectionChange(currentIds.filter(id => !filteredIds.includes(id)));
    } else {
      // Select all filtered options (keeping existing selections)
      const newIds = filteredIds.filter(id => !currentIds.includes(id));
      this.handleSelectionChange([...currentIds, ...newIds]);
    }

    if (this.clearSearchOnSelect()) {
      this.search.set('');
    }
  }

  public toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  public open(): void {
    if (this.isDisabled() || this.isOpen()) {
      return;
    }
    this.isOpen.set(true);
    this.dropdownOpened();
  }

  public close(): void {
    if (!this.isOpen()) {
      return;
    }
    this.isOpen.set(false);
    this.dropdownClosed();
    this.onTouched();
  }

  // The CDK overlay's outside-click dispatcher can misreport an in-menu click as
  // "outside" when the clicked option is detached mid-click by an upstream
  // re-render (e.g. a sibling table re-rendering under an open filter). Guard
  // geometrically so only clicks that truly land outside the menu and trigger
  // close the dropdown.
  public onOverlayOutsideClick(event: MouseEvent): void {
    const point = { x: event.clientX, y: event.clientY };
    if (
      this.isPointInElement(point, this.menuRef()?.nativeElement) ||
      this.isPointInElement(point, this.triggerOrigin()?.elementRef.nativeElement)
    ) {
      return;
    }
    this.close();
  }

  public onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      EventUtils.preventBubble(event);
      this.close();
    }
  }

  private isPointInElement(
    point: { x: number; y: number },
    element?: HTMLElement | null,
  ): boolean {
    if (!element) {
      return false;
    }
    const rect = element.getBoundingClientRect();
    return (
      point.x >= rect.left &&
      point.x <= rect.right &&
      point.y >= rect.top &&
      point.y <= rect.bottom
    );
  }

  public dropdownOpened(): void {
    this.activeIndex.set(-1);
    if (this.enableSearch()) {
      setTimeout(() => {
        this.searchInputRef()?.nativeElement.focus();
      });
    }
  }

  public dropdownClosed() {
    this.search.set('');
    this.activeIndex.set(-1);
  }

  private moveActiveIndex(delta: number): void {
    const options = this.filteredListOptions();
    if (options.length === 0) {
      return;
    }
    const maxIndex = options.length - 1;
    const current = this.activeIndex();
    let next: number;
    if (delta > 0) {
      next = current < maxIndex ? current + 1 : 0;
    } else {
      next = current > 0 ? current - 1 : maxIndex;
    }
    this.setActiveIndex(next);
  }

  private setActiveIndex(index: number): void {
    this.activeIndex.set(index);
    this.scrollToActiveIndex(index);
  }

  private scrollToActiveIndex(index: number): void {
    const viewport = this.virtualScrollViewport();
    if (!viewport) {
      return;
    }

    const itemSize = this.virtualItemSize();
    const viewportSize = viewport.getViewportSize();
    const currentOffset = viewport.measureScrollOffset();

    const targetOffset = index * itemSize;
    const isAboveViewport = targetOffset < currentOffset;
    const isBelowViewport = targetOffset + itemSize > currentOffset + viewportSize;

    if (isAboveViewport) {
      viewport.scrollToIndex(index);
    } else if (isBelowViewport) {
      viewport.scrollToOffset(targetOffset - viewportSize + itemSize);
    }
  }

  private selectActiveOption(): void {
    const options = this.filteredListOptions();
    const index = this.activeIndex();

    if (index < 0 || index >= options.length) {
      return;
    }

    this.onOptionSelected(options[index].value);

    if (this.clearSearchOnSelect()) {
      this.search.set('');
    }
  }

  get errorMessages() {
    const errors = this.ngControl.control?.errors;
    const errorMessages = [] as string[];

    if (!errors) {
      return errorMessages;
    } else {
      Object.keys(errors).forEach(key => {
        const error = 'There seems to be an error with this field.';

        const customError = this.customErrorMessages()[key];

        if (customError) {
          errorMessages.push(customError);
          return;
        }

        switch (key) {
          case 'required': {
            errorMessages.push('Please select an item.');
            break;
          }
          default: {
            errorMessages.push(error);
          }
        }
      });
    }

    return errorMessages;
  }
}
